import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Authing } from '@authing/web';
import { identifyUser, resetIdentity } from '../../utils/posthogIdentify';
import { setSentryUser, clearSentryUser } from '../../utils/sentryLoader';
import { getUtmData, clearUtmData } from '../../utils/utmCapture';
import {
  trackSignupCompleted,
  trackAppSessionStarted,
  trackServerHealthPing,
  trackBootstrapFetch,
  trackLoginInitiated,
  trackLoginCallbackReceived,
  trackLoginCodeExchangeSuccess,
  trackLoginCodeExchangeFailed,
  trackLoginSessionEstablished,
} from '../../utils/posthogEvents';
import { logger } from '../../utils/logger';
import { reportUserFacingError } from '../../utils/errorReporting';
import { exchangeAuthCode, getCurrentUser, getCurrentUserWithStatus, fetchBootstrap, checkSession } from '../../shared/api/auth';
import { getEnrollmentStatus } from '../../features/studentDashboard';
import { getFamilyMembers } from '../../features/families';
import { setAuthInitInProgress } from '../../shared/api/authState';
import { mark } from '../../utils/bootTiming';
import { getAppBase, stripAppBase } from '../../utils/appBase';

const AuthContext = createContext();

let authingInstance = null;

// Default enrollment status when not enrolled or on error
const DEFAULT_ENROLLMENT = { isEnrolled: false, isTeacher: false, hasAccess: false, blockedCourses: [], loading: false };

// Built from the runtime prefix so one build works at / and under /homework
// (see docs/plans/move-app-to-root.md). Both shapes are on the Authing
// whitelist and in the backend's ALLOWED_REDIRECT_URIS.
function getCallbackPath() {
  return `${getAppBase()}/auth/callback`;
}

function ensureAuthingInstance() {
  if (!authingInstance) {
    const callbackPath = getCallbackPath();
    authingInstance = new Authing({
      domain: import.meta.env.VITE_AUTHING_DOMAIN,
      appId: import.meta.env.VITE_AUTHING_APP_ID,
      redirectUri: `${window.location.origin}${callbackPath}`,
      scope: 'openid profile email offline_access',
    });
    window.authingInstance = authingInstance;
  }
  return authingInstance;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_user');
      return cached ? JSON.parse(cached) : null;
    } catch (e) { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [isSignedIn, setIsSignedIn] = useState(() => {
    try {
      return localStorage.getItem('isAuthenticated') === 'true';
    } catch (e) { return false; }
  });
  const [enrollmentStatus, setEnrollmentStatus] = useState({
    isEnrolled: false,
    isTeacher: false,
    hasAccess: false,
    blockedCourses: [],
    loading: true
  });
  const [familyMembers, setFamilyMembers] = useState([]);
  const [effectiveStudentId, setEffectiveStudentId] = useState(() => {
    try {
      return localStorage.getItem('effective_student_id') || null;
    } catch (e) { return null; }
  });
  // Dashboard payload prefetched by /api/bootstrap on cold boot. Consumed
  // by useDashboardData as the initial render state — saves the separate
  // /students/:id/dashboard round trip on first paint. Ref instead of state
  // because consumption is one-shot: after the first read, further hook
  // mounts should fall through to a fresh fetch rather than replay stale data.
  const prefetchedDashboardRef = useRef(null);
  // Phase 5: streaks payload prefetched by /api/bootstrap alongside the
  // dashboard. useStreakData consumes it via consumePrefetchedStreaks.
  // Enrollment + family are applied directly to React state in
  // setupUserSession (no consumer handoff needed).
  const prefetchedStreaksRef = useRef(null);
  // In-flight bootstrap promise. Populated BEFORE awaiting fetchBootstrap so
  // that OptimisticProtectedRoute-mounted consumers can await the same
  // promise instead of firing a duplicate /students/:id/dashboard fetch.
  // Resolves to { dashboard, fetchedAt } on success, null otherwise.
  const inflightBootstrapRef = useRef(null);
  const isProcessingCallbackRef = useRef(false);

  // Capture auth code during RENDER (before any effects run).
  // AuthCallback (child) navigates away immediately in its effect,
  // changing the URL. Parent renders before children, so this ref
  // captures the code before AuthCallback's effect can clear it.
  const capturedAuthParams = useRef(null);
  if (!capturedAuthParams.current) {
    // ONLY on the callback path. Authing always returns to the registered
    // redirect URI, so a `?code=` anywhere else belongs to someone else — and it
    // does: a redemption link is `/kidsSongs/claim?code=LAMB-7YM5-77UR`. Treating
    // that as a login POSTed the gift-card number to the token endpoint (400s,
    // 90 of them across 18 people before this guard) and, worse, took the
    // callback branch below, skipping the returning-visitor session restore on
    // the one page where a paying parent lands. Match the suffix, not the whole
    // path: the app serves from both `/` and `/homework/`.
    const onCallbackPath = window.location.pathname.replace(/\/+$/, '').endsWith('/auth/callback');
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    capturedAuthParams.current = {
      code: onCallbackPath ? (urlParams.get('code') || hashParams.get('code')) : null,
      state: onCallbackPath ? (urlParams.get('state') || hashParams.get('state')) : null,
    };
  }

  // Helper to clear all auth state (localStorage + React state)
  const clearAuthState = () => {
    try {
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('cached_user');
      localStorage.removeItem('effective_student_id');
    } catch (e) { /* storage unavailable */ }
    setUser(null);
    setIsSignedIn(false);
    setEnrollmentStatus(DEFAULT_ENROLLMENT);
    setFamilyMembers([]);
    setEffectiveStudentId(null);
  };

  // Helper to set up user state after successful authentication
  // Accepts optional prefetched data — when bootstrap supplies enrollment /
  // family / streaks, we skip the individual fetches below (saves 2-3 RTTs
  // on every returning-visitor cold boot). Missing fields fall through to
  // the legacy fire-and-forget path, so callers without prefetch (auth-code
  // callback, slow-path checkSession) behave exactly as before.
  const setupUserSession = async (userData, prefetched = {}) => {
    setUser(userData);
    setIsSignedIn(true);
    try {
      localStorage.setItem('isAuthenticated', 'true');
      // Strip ephemeral is_new_user flag before caching — it's a per-request signal
      // and must not be replayed from localStorage on subsequent page loads
      const { is_new_user: _ignored, ...cacheable } = userData || {};
      localStorage.setItem('cached_user', JSON.stringify(cacheable));
    } catch (e) { /* storage unavailable, ignore */ }

    // Identify user in PostHog for analytics + persist UTM attribution
    const utmData = getUtmData();
    if (userData?.id) {
      identifyUser(userData.id, {
        name: userData.name,
        email: userData.email,
        role: userData.role || 'student',
        // Cohort segment (self_study/library vs class_student vs staff), derived
        // server-side and shipped on the user payload by /api/auth/me + /api/bootstrap.
        ...(userData.customer_type && { customer_type: userData.customer_type }),
        ...(utmData && { utm_source: utmData.utm_source, utm_medium: utmData.utm_medium }),
      });
      // Mark the start of an authenticated session for DAU/MAU measurement
      trackAppSessionStarted({ role: userData.role || 'student' });
      // Fire-and-forget server health snapshot once per session. Uses raw fetch
      // (not apiRequest) so it doesn't pollute api_request_completed latency data.
      // Parses the body regardless of res.ok — backend intentionally includes
      // system metrics on 503 responses so we can see server health during outages.
      // try/catch guards against sync throws (e.g. test stubs where fetch returns
      // undefined and `.then()` is called on undefined).
      try {
        const apiUrlForHealth = import.meta.env.VITE_API_URL || '/api';
        const healthUrl = `${apiUrlForHealth.replace(/\/api\/?$/, '')}/health`;
        fetch(healthUrl)
          .then(res => res.json().catch(() => null))
          .then(data => {
            if (data) {
              trackServerHealthPing({
                memoryRssMb: data.memory_rss_mb,
                heapUsedMb: data.heap_used_mb,
                heapTotalMb: data.heap_total_mb,
                cpuLoad1m: data.cpu_load_1m,
                freeMemMb: data.free_mem_mb,
                totalMemMb: data.total_mem_mb,
                uptimeSeconds: data.uptime,
              });
            }
          })
          .catch(() => {});
      } catch { /* silent — health ping is best-effort */ }
    }
    // Track signup (only fires on first login after account creation)
    if (userData?.is_new_user) {
      trackSignupCompleted({
        userId: userData.authing_user_id,
        source: utmData?.utm_source,
      });
    }
    // Persist UTMs to backend (first-touch, fire-and-forget)
    if (utmData) {
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      fetch(`${apiUrl}/auth/utm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(utmData),
        credentials: 'include',
      }).then(res => { if (res.ok) clearUtmData(); }).catch(() => {});
    }

    // Set Sentry user context for error tracking.
    // `id` matches PostHog's distinct_id (userData.id) so that cross-tool
    // lookups resolve to the same person. `username` carries the authing
    // user ID which is still searchable and appears in our admin URLs.
    // setSentryUser safely queues the user if Sentry hasn't lazy-loaded
    // yet — critical because Sentry loads on first interaction, which may
    // come AFTER login completes on returning sessions.
    // Skip entirely if id is missing — otherwise String(undefined) would
    // tag all errors with the literal string "undefined" and break search.
    if (userData?.id != null) {
      setSentryUser({
        id: String(userData.id),
        username: userData.authing_user_id,
        email: userData.email,
        segment: userData.name,
      });
    }

    // Set effectiveStudentId: use saved value if still valid, otherwise default to self
    const savedEffective = localStorage.getItem('effective_student_id');
    if (!savedEffective || savedEffective === userData.authing_user_id) {
      setEffectiveStudentId(userData.authing_user_id);
    }
    // If savedEffective is a different ID, we'll validate it after family members load

    // Extracted so both the prefetch-hit and prefetch-miss paths share the
    // family-members handling (including effectiveStudentId validation).
    const applyFamily = (data) => {
      const members = data?.members || [];
      setFamilyMembers(members);
      if (savedEffective && savedEffective !== userData.authing_user_id) {
        const isAccessible = members.some(m => m.authing_user_id === savedEffective);
        if (isAccessible) {
          setEffectiveStudentId(savedEffective);
        } else {
          setEffectiveStudentId(userData.authing_user_id);
          try { localStorage.removeItem('effective_student_id'); } catch (e) { /* */ }
        }
      }
    };

    // Enrollment: use bootstrap-supplied payload if present; else fetch.
    if (prefetched.enrollmentStatus) {
      setEnrollmentStatus({ ...prefetched.enrollmentStatus, loading: false });
    } else {
      getEnrollmentStatus(userData.authing_user_id)
        .then(status => setEnrollmentStatus({ ...status, loading: false }))
        .catch(enrollErr => {
          logger.error('Error fetching enrollment status:', enrollErr);
          // error: "not known", which DashboardRedirect must not read as "in no class"
          setEnrollmentStatus({ ...DEFAULT_ENROLLMENT, error: true });
        });
    }

    // Family: use bootstrap-supplied payload if present; else fetch.
    if (prefetched.family) {
      applyFamily(prefetched.family);
    } else {
      getFamilyMembers()
        .then(applyFamily)
        .catch(() => setFamilyMembers([]));
    }
  };

  // Helper to set up fallback user when real user data fetch fails
  const setupFallbackUser = () => {
    setUser({ id: 'authenticated', name: 'User' });
    setIsSignedIn(true);
    try {
      localStorage.setItem('isAuthenticated', 'true');
    } catch (e) { /* storage unavailable */ }
    setEnrollmentStatus(DEFAULT_ENROLLMENT);
  };

  // Fetch user data and set up session, falling back gracefully on error
  const fetchAndSetupUser = async () => {
    try {
      const userData = await getCurrentUser();
      await setupUserSession(userData);
    } catch (error) {
      reportUserFacingError(error, { context: 'Auth: fetch current user' });
      // Use cached user if available (better than generic fallback)
      const cached = localStorage.getItem('cached_user');
      if (cached) {
        try {
          await setupUserSession(JSON.parse(cached));
          return;
        } catch (e) { /* fall through to fallback */ }
      }
      setupFallbackUser();
    }
  };

  useEffect(() => {
    const initAuthing = async () => {
      mark('auth_init_start');
      // Skip auth initialization if we're on the logout page
      const isLogoutPage = window.location.pathname === '/logout' || window.location.pathname === '/homework/logout';
      if (isLogoutPage) {
        setLoading(false);
        return;
      }

      // Skip auth initialization if not configured for local development
      if (!import.meta.env.VITE_AUTHING_DOMAIN || !import.meta.env.VITE_AUTHING_APP_ID) {
        setLoading(false);
        return;
      }

      // Use auth code captured during render (before AuthCallback navigates away)
      const authCode = capturedAuthParams.current.code;
      const state = capturedAuthParams.current.state;

      // Check if user just logged out (within last 5 seconds)
      // This prevents auto-login from Authing SSO - but NOT if there's an auth code
      // (auth code means user explicitly logged in again via the login form)
      const justLoggedOut = sessionStorage.getItem('just_logged_out');
      if (justLoggedOut && !authCode) {
        const logoutTime = parseInt(justLoggedOut, 10);
        const now = Date.now();
        const timeSinceLogout = now - logoutTime;
        if (timeSinceLogout < 5000) {
          sessionStorage.removeItem('just_logged_out');
          setLoading(false);
          return;
        }
      }
      // Clear stale flag (always, even if we're processing a code)
      if (justLoggedOut) {
        sessionStorage.removeItem('just_logged_out');
      }

      const callbackPath = getCallbackPath();

      setAuthInitInProgress(true);
      try {
        ensureAuthingInstance();

        if (authCode) {
          // Guard against concurrent callback processing (React StrictMode, race conditions)
          if (isProcessingCallbackRef.current) {
            setLoading(false);
            return;
          }
          isProcessingCallbackRef.current = true;

          // Read login_initiated_at AND login_correlation_id stashed by
          // signIn() so we can compute end-to-end total_login_ms and attach
          // the same correlation id to every funnel event from here forward.
          const loginInitiatedAt = (() => {
            try {
              const raw = sessionStorage.getItem('login_initiated_at');
              return raw ? parseInt(raw, 10) : null;
            } catch (e) { return null; }
          })();
          const correlationId = (() => {
            try { return sessionStorage.getItem('login_correlation_id'); }
            catch (e) { return null; }
          })();

          try {
            // Retrieve PKCE code_verifier from sessionStorage (stored by Authing SDK)
            // The Authing SDK stores it with key pattern: authing-spa:1:{appId}:tx:{state}
            let codeVerifier = null;

            if (state && import.meta.env.VITE_AUTHING_APP_ID) {
              const txKey = `authing-spa:1:${import.meta.env.VITE_AUTHING_APP_ID}:tx:${state}`;
              const txData = sessionStorage.getItem(txKey);
              if (txData) {
                try {
                  const parsed = JSON.parse(txData);
                  codeVerifier = parsed.code_verifier || parsed.codeVerifier;
                } catch (e) {
                  reportUserFacingError(e, { context: 'Auth: parse PKCE transaction data' });
                }
              }
            }

            trackLoginCallbackReceived({
              hasPkce: !!codeVerifier,
              statePrefix: state?.slice(0, 8) || null,
              correlationId,
            });

            // Use BFF pattern - send code, code_verifier, AND state to backend
            const codeExchangeStart = Date.now();
            const data = await exchangeAuthCode({
              code: authCode,
              redirectUri: `${window.location.origin}${callbackPath}`,
              codeVerifier,
              state
            });

            // Backend now sets sid cookie - preload destination chunk + fetch user data
            if (data.success) {
              trackLoginCodeExchangeSuccess({
                durationMs: Date.now() - codeExchangeStart,
                correlationId,
              });

              // Preload target page chunk in parallel with getCurrentUser
              const savedRedirect = localStorage.getItem('auth_redirect_url');
              if (!savedRedirect || savedRedirect === '/' || savedRedirect.startsWith('/dashboard')) {
                import('../../features/studentDashboard').catch(() => {});
              } else if (savedRedirect.startsWith('/qr/')) {
                import('../../features/lessonViewer').catch(() => {});
              }

              await fetchAndSetupUser();

              if (loginInitiatedAt) {
                trackLoginSessionEstablished({
                  totalLoginMs: Date.now() - loginInitiatedAt,
                  correlationId,
                });
                try {
                  sessionStorage.removeItem('login_initiated_at');
                  sessionStorage.removeItem('login_correlation_id');
                } catch (e) { /* */ }
              }
            }
          } catch (error) {
            trackLoginCodeExchangeFailed({
              errorName: error?.name || 'Error',
              errorMessageSnippet: error?.message?.slice(0, 100) || null,
              errorStatus: error?.status ?? null,
              errorBody: error?.body ?? null,
              correlationId,
            });
            reportUserFacingError(error, { context: 'Auth: BFF auth code exchange' });
          } finally {
            // Always reset processing flag, even if redirect happens or error occurs
            isProcessingCallbackRef.current = false;
          }
        } else {
          // No auth code — returning visitor boot.
          //
          // Fast path: if localStorage says we were authenticated last time,
          // skip the /auth/health pre-check and go straight to /auth/me.
          // Saves ~1 Hangzhou RTT (100-200ms) off cold boot for most users.
          // Slow path (checkSession → fetchAndSetupUser) is preserved for
          // fresh devices + just-logged-out users.
          let cachedUserParsed = null;
          try {
            const raw = localStorage.getItem('cached_user');
            if (raw && localStorage.getItem('isAuthenticated') === 'true') {
              cachedUserParsed = JSON.parse(raw);
            }
          } catch (e) { /* corrupted cache → fall through to slow path */ }

          if (cachedUserParsed) {
            mark('auth_health_skipped');

            // Phase 3: /api/bootstrap returns user + dashboard in one RTT.
            // Opt-out via localStorage flag lets us disable without redeploy
            // if prod issues surface.
            const bootstrapOptOut = (() => {
              try { return localStorage.getItem('bootstrap_disabled') === 'true'; }
              catch (e) { return false; }
            })();

            // Pass the student the UI will actually render (family-switched
            // or self). Backend checkStudentAccess enforces the access rule
            // regardless — if the saved effective ID is stale (family member
            // removed), the 403 path below handles it gracefully.
            const bootstrapStudentId = (() => {
              try { return localStorage.getItem('effective_student_id') || cachedUserParsed.authing_user_id; }
              catch (e) { return cachedUserParsed.authing_user_id; }
            })();

            let result;
            const bootstrapStart = Date.now();
            // fetchBootstrap bypasses the shared api_request tracker — capture
            // latency/status/size at the call site so /api/bootstrap is
            // observable in PostHog. Routes through trackBootstrapFetch (not a
            // direct window.posthog.capture) because bootstrap resolves during
            // the cold-boot window when PostHog may still be lazy-loading;
            // the tracker queues events until PostHog attaches.
            // Opt-out path calls /me, not /bootstrap, so it stays silent.
            const captureBootstrapFetch = (status, data) => {
              if (bootstrapOptOut) return;
              trackBootstrapFetch({
                status,
                durationMs: Date.now() - bootstrapStart,
                studentId: bootstrapStudentId,
                payloadSize: data ? JSON.stringify(data).length : 0,
              });
            };
            try {
              mark('bootstrap_fetch_start');
              if (bootstrapOptOut) {
                result = await getCurrentUserWithStatus();
              } else {
                // Register the in-flight promise BEFORE awaiting so
                // useDashboardData mounts during the await-gap can attach to
                // it instead of firing their own /students/:id/dashboard fetch.
                const pending = fetchBootstrap(bootstrapStudentId);
                inflightBootstrapRef.current = {
                  studentId: bootstrapStudentId,
                  promise: pending.then(
                    (r) => (r && r.status === 200 && r.data && r.data.dashboard)
                      ? { dashboard: r.data.dashboard, fetchedAt: r.data.fetchedAt || Date.now() }
                      : null,
                    () => null,
                  ),
                };
                result = await pending;
              }
              mark('bootstrap_fetch_complete', { status: result.status, optOut: bootstrapOptOut });
              captureBootstrapFetch(result.status, result.data);
            } catch (netErr) {
              // Network / timeout / DNS — not a signal about session validity.
              // Preserve cached user so the app boots during transient outages.
              mark('auth_me_network_error');
              captureBootstrapFetch(0, null);
              // The inflight promise already resolved to null via its error
              // handler, but the ref itself needs clearing so later mounts
              // don't attach to a promise that no longer represents fresh work.
              inflightBootstrapRef.current = null;
              await setupUserSession(cachedUserParsed);
              return;
            }

            // Bootstrap settled — the in-flight ref is no longer the source of
            // truth. Cleared on every branch below (not just 200) so stale
            // promises don't leak into later hook mounts.
            inflightBootstrapRef.current = null;

            if (result.status === 200 && result.data) {
              // Bootstrap shape: { user, dashboard, streaks, enrollmentStatus,
              //                    family, hasSession, fetchedAt }.
              // Opt-out shape: AuthMeResponse directly (just the user) — no
              // dashboard/streaks/enrollment/family, so prefetch refs stay null
              // and setupUserSession's fallback fetches fire as pre-Phase-5.
              const userPayload = bootstrapOptOut ? result.data : result.data.user;
              const bootstrapData = bootstrapOptOut ? {} : result.data;

              if (bootstrapData.dashboard) {
                prefetchedDashboardRef.current = {
                  studentId: bootstrapStudentId,
                  dashboard: bootstrapData.dashboard,
                  fetchedAt: bootstrapData.fetchedAt || Date.now(),
                };
                mark('bootstrap_dashboard_prefetched');
              }
              if (bootstrapData.streaks) {
                prefetchedStreaksRef.current = {
                  studentId: bootstrapStudentId,
                  streaks: bootstrapData.streaks,
                };
              }
              await setupUserSession(userPayload, {
                enrollmentStatus: bootstrapData.enrollmentStatus,
                family: bootstrapData.family,
              });
            } else if (result.status === 401) {
              // Session genuinely expired (user logged out elsewhere, TTL hit,
              // etc). Clear state; OptimisticProtectedRoute triggers signIn().
              clearAuthState();
            } else if (result.status === 403) {
              // Access denied — saved effectiveStudentId is stale (family
              // member removed, etc). Clear the stale ID synchronously BEFORE
              // setupUserSession so useDashboardData can't start a fetch
              // against the wrong studentId during the window while
              // getFamilyMembers resolves asynchronously.
              try { localStorage.removeItem('effective_student_id'); }
              catch (e) { /* storage unavailable */ }
              await setupUserSession(cachedUserParsed);
            } else {
              // 5xx or unexpected status — preserve cache, same as network err.
              await setupUserSession(cachedUserParsed);
            }
          } else {
            // Slow path: no cached user → need health check to avoid 401 noise.
            // check_session_start also serves as the "slow path taken" count
            // for computing adoption rate vs auth_health_skipped in PostHog.
            mark('check_session_start');
            const health = await checkSession();
            mark('check_session_end', { hasSession: !!health?.hasSession });

            if (health.hasSession) {
              await fetchAndSetupUser();
            } else {
              clearAuthState();
            }
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          logger.warn('Auth health check timed out — falling back to cache');
        } else {
          logger.error('Authing initialization error:', error);
        }
        // Don't clear auth state if we have cached data — network might just be slow
        const cachedUser = localStorage.getItem('cached_user');
        const wasAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
        if (wasAuthenticated && cachedUser) {
          try {
            await setupUserSession(JSON.parse(cachedUser));
          } catch (e) {
            clearAuthState();
          }
        } else {
          clearAuthState();
        }
      } finally {
        setAuthInitInProgress(false);
        setLoading(false);
        mark('auth_init_end');
      }
    };

    initAuthing();
  }, []);

  const signIn = useCallback(async () => {
    // Prevent 401 handler from hard-redirecting while we redirect to Authing.
    // Without this, optimistically-rendered pages fire API calls that 401,
    // causing a redirect to / which overwrites auth_redirect_url.
    setAuthInitInProgress(true);
    try {
      ensureAuthingInstance();

      // Store the current URL for post-login redirect, minus the serving
      // prefix (the router basename adds it back).
      let currentPath = stripAppBase(window.location.pathname + window.location.search);

      // Don't redirect back to logout page after login - go to home instead
      if (currentPath === '/logout') {
        currentPath = '/';
      }

      localStorage.setItem('auth_redirect_url', currentPath);

      // Stash the login start time AND a correlation UUID so the callback
      // handler can compute end-to-end total_login_ms AND attach the same id
      // to every funnel event. PostHog funnels can't follow distinct_id
      // across anonymous→identified rotation, so we correlate on a property.
      const correlationId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      try {
        sessionStorage.setItem('login_initiated_at', Date.now().toString());
        sessionStorage.setItem('login_correlation_id', correlationId);
      } catch (e) { /* storage unavailable */ }
      trackLoginInitiated({ redirectUrl: currentPath, correlationId });

      await authingInstance.loginWithRedirect();
      // Browser navigates away — this line never executes
    } catch (e) {
      setAuthInitInProgress(false);
      throw e;
    }
  }, []);

  const switchStudent = useCallback((newStudentId) => {
    setEffectiveStudentId(newStudentId);
    try {
      localStorage.setItem('effective_student_id', newStudentId);
    } catch (e) { /* storage unavailable */ }
  }, []);

  const signUp = useCallback(async () => {
    // For hosted login, signup and signin use the same flow
    return signIn();
  }, [signIn]);

  const signOut = useCallback(async () => {
    const isDevelopment =
      import.meta.env.MODE === 'development' || window.location.hostname === 'localhost';

    // Use the configured API URL (respects VITE_API_URL if set)
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    const logoutRedirectUri = `${window.location.origin}${getAppBase()}/logout`;

    // Clear server session and get id_token for SSO logout
    let idToken = null;
    try {
      const response = await fetch(`${apiUrl}/auth/logout`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();
      idToken = data.idToken;
    } catch (error) {
      // Non-fatal - continue with logout even if server call fails
    }

    // Clear client state
    try {
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('cached_user');
      localStorage.removeItem('effective_student_id');
      const sessionKey = `authing-spa:1:${import.meta.env.VITE_AUTHING_APP_ID}:login-state`;
      localStorage.removeItem(sessionKey);

      // Set a flag to prevent auto-login for a short period
      // This prevents the race condition where Authing SSO logs us back in
      sessionStorage.setItem('just_logged_out', Date.now().toString());
    } catch (storageError) {
      // Ignore storage errors
    }

    setUser(null);
    setIsSignedIn(false);

    // Reset PostHog identity on logout
    resetIdentity();

    // Clear Sentry user context (also clears any pending queued user)
    clearSentryUser();

    // Redirect to Authing SSO logout to end their session
    // This prevents silent re-login when user clicks "重新登录"
    // NOTE: May not work for WeChat logins (Authing limitation), but will still redirect to logout page
    if (idToken && import.meta.env.VITE_AUTHING_DOMAIN) {
      const authingLogoutUrl = `https://${import.meta.env.VITE_AUTHING_DOMAIN}/oidc/session/end?` +
        `id_token_hint=${encodeURIComponent(idToken)}&` +
        `post_logout_redirect_uri=${encodeURIComponent(logoutRedirectUri)}`;
      window.location.href = authingLogoutUrl;
    } else {
      // Fallback if no id_token available (session already expired, etc.)
      window.location.href = logoutRedirectUri;
    }
  }, []);

  // Role helper methods - use explicit role from database, default to 'student'
  const isTeacher = useCallback(() => {
    const role = user?.role || 'student';
    return role === 'teacher' || role === 'admin';
  }, [user]);

  const isAdmin = useCallback(() => {
    const role = user?.role || 'student';
    return role === 'admin';
  }, [user]);

  const value = {
    user,
    isSignedIn,
    loading,
    enrollmentStatus,
    signIn,
    signUp,
    signOut,
    authingInstance,
    // Role helpers
    isTeacher,
    isAdmin,
    // Family account switching
    effectiveStudentId: effectiveStudentId || user?.authing_user_id || null,
    familyMembers,
    switchStudent,
    // Phase 3 cold-boot prefetch — useDashboardData calls this on mount to
    // hydrate from the /api/bootstrap payload without firing a second RTT.
    // Returns one of:
    //   - { studentId, dashboard, fetchedAt } — resolved prefetch, one-shot
    //     (ref cleared after consumption so later mounts fetch fresh data).
    //   - { promise } — bootstrap still in flight for this studentId; caller
    //     awaits and hydrates when it resolves (or null on failure). NOT
    //     one-shot: StrictMode double-invocation is safe because resolved
    //     values are immutable and the next cold boot overwrites the ref.
    //   - null — no matching prefetch or in-flight bootstrap.
    consumePrefetchedDashboard: useCallback((studentId) => {
      const prefetch = prefetchedDashboardRef.current;
      if (prefetch && prefetch.studentId === studentId) {
        prefetchedDashboardRef.current = null;
        return prefetch;
      }
      const inflight = inflightBootstrapRef.current;
      if (inflight && inflight.studentId === studentId) {
        return { promise: inflight.promise };
      }
      return null;
    }, []),
    // Phase 5 — useStreakData consumes this on mount; one-shot to prevent
    // replay after a later student switch. Returns { studentId, streaks } on
    // hit, null on miss. No in-flight promise shape because streaks aren't
    // critical-path for first paint (badge shows a placeholder on miss).
    consumePrefetchedStreaks: useCallback((studentId) => {
      const prefetch = prefetchedStreaksRef.current;
      if (prefetch && prefetch.studentId === studentId) {
        prefetchedStreaksRef.current = null;
        return prefetch;
      }
      return null;
    }, []),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useUser = () => {
  const { user, loading } = useAuth();
  return { user, isLoaded: !loading };
};
